// src/components/SharedConversation.jsx

import { useRef, useEffect, useState } from "react";

const API_BASE = "http://localhost:4000";
//const API_BASE = "https://myjourneytrails.com";

export default function SharedConversation({
  token,
  messages,
  setMessages,
  sessionEnded,
  isJournaler,
  journalerName,
  companionName
}) {
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState("");
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const bottomRef = useRef(null);

  const sender = isJournaler ? "user" : "companion";
  const canSend = note.trim().length > 0 && !sending && !sessionEnded;

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  async function handleSend(e) {
    e.preventDefault();
    if (!canSend) return;

    try {
      setSending(true);
      setStatus("");

      const res = await fetch(`${API_BASE}/api/shared/${token}/message`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ sender, message: note.trim() })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to send");

      setNote("");
      setStatus("");
    } catch (err) {
      setStatus("Failed: " + err.message);
    } finally {
      setSending(false);
    }
  }

  async function handleClear() {
    try {
      const res = await fetch(`${API_BASE}/api/shared/${token}/clear`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ sender })
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Clear failed: ${res.status}`);
      }

      setShowClearConfirm(false);
      setStatus("Cleared ✓");
    } catch (err) {
      setStatus("Clear failed: " + err.message);
    }
  }

  return (
    <div className="chat-wrap">
      <div className="chat-history">
        {messages.length === 0 ? (
          <div className="chat-empty">No messages yet.</div>
        ) : (
          messages.map((msg, idx) => {
            const isMine =
              (isJournaler && msg.sender === "user") ||
              (!isJournaler && msg.sender === "companion");

            return (
              <div
                className={`chat-bubble ${isMine ? "mine" : "theirs"}`}
                key={`${msg.sent_at || idx}-${idx}`}
              >
                <div className="chat-meta">
                  {msg.sender === "user" ? journalerName : companionName}
                </div>

                <div className="chat-text">
                  {msg.message}
                </div>

                {msg.sent_at && (
                  <div className="chat-time">
                    {new Date(msg.sent_at).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit"
                    })}
                  </div>
                )}
              </div>
            );
          })
        )}
        <div ref={bottomRef} />
      </div>

      <form className="chat-form" onSubmit={handleSend}>
        {!sessionEnded && (
          <>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Write a message…"
              rows={3}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSend(e);
                }
              }}
            />

            <div className="chat-actions">
              {status && <span className="chat-status">{status}</span>}

              {isJournaler && (
                <button
                  type="button"
                  onClick={() => setShowClearConfirm(true)}
                  className="clear-btn"
                >
                  Clear My Messages
                </button>
              )}

              <button type="submit" disabled={!canSend}>
                {sending ? "Sending…" : "Send"}
              </button>
            </div>
          </>
        )}
      </form>

      {showClearConfirm && (
        <div className="clear-confirm">
          <p>Clear all your messages?</p>
          <div className="confirm-buttons">
            <button type="button" onClick={handleClear}>Yes, clear</button>
            <button type="button" onClick={() => setShowClearConfirm(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
